import type { Unsubscribe } from 'firebase/firestore';
import { OutletDirectorySchema, type OutletDirectoryRow } from '@kilat/shared';
import { createRepo } from './_base.js';

/**
 * outletDirectory/{outletId} — public read of LISTED rows; a super-admin
 * additionally reads their own brand's UNLISTED rows (firestore.rules).
 *
 * Super-admin curation must list through the brand-scoped helpers below,
 * never `listSnapshot()`/`list()`. Firestore evaluates a LIST query against
 * the rule rather than filtering its results, so an unfiltered query is denied
 * outright the moment one unlisted row could be in it — the rule cannot prove
 * every row belongs to the caller's brand. `where('platformBrandId','==',b)`
 * is what lets it prove that (infra/test/superadmin-list-queries.rules.test.ts).
 */
export const outletDirectoryRepo = createRepo({
  path: 'outletDirectory',
  schema: OutletDirectorySchema,
  name: 'outletDirectory',
});

/** Live list of every directory row (listed or not) of one platform brand. */
export function subscribeDirectoryForBrand(
  brandId: string,
  cb: (rows: OutletDirectoryRow[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return outletDirectoryRepo.listSnapshotBounded(
    { where: [['platformBrandId', '==', brandId]] },
    cb,
    onError,
  );
}

/** One-shot read of every directory row of one platform brand. */
export function listDirectoryForBrand(brandId: string): Promise<OutletDirectoryRow[]> {
  return new Promise((resolve, reject) => {
    let unsub: Unsubscribe | null = null;
    let settled = false;
    const finish = (): void => {
      settled = true;
      if (unsub) unsub();
    };
    unsub = subscribeDirectoryForBrand(
      brandId,
      (rows) => {
        if (settled) return;
        finish();
        resolve(rows);
      },
      (err) => {
        if (settled) return;
        finish();
        reject(err);
      },
    );
    // The first snapshot can arrive synchronously from cache before `unsub`
    // is assigned; tear down now that we hold the handle.
    if (settled) unsub();
  });
}
